import { request } from "../core/request";
import { REST_CONNECTION_FROM } from "../constants/config";
import type { MultiAssetApiResponse, MultiAssetApiRequest } from "../types/multiassetapiTypes";
import { OPERATION_IDS } from "./constants";

/**
 * OfferMart BRE Multi-Asset API Service
 *
 * Purpose: Fetch offers from OfferMart BRE API based on user details and filter criteria
 * This service is used to provide personalized product offers to users
 *
 * Only called for authenticated users with valid mobile number
 *
 * Features:
 * - Accepts user details and filter criteria
 * - Returns structured offer list with personalized recommendations
 * - Used in B2B CLP pages to display relevant product offers
 *
 * @param params - Request payload containing:
 *   - dealer_ids: Dealer identifier
 *   - product: Product type
 *   - channel_flag: Channel identifier
 *   - mobile_number: User's mobile number
 *   - req_date: Request date in YYYY-MM-DD format
 *   - subscription: Subscription status
 *   - city: User's city
 *   - filter: Object containing loan and product filters
 *
 * @returns Promise resolving to offers response with offers_list and status
 *   - Returns API response on success
 *   - Returns error response with empty offers_list on failure
 *
 * @throws Does not throw - returns error response object instead
 */
export async function fetchFloatingOffers(
  params: MultiAssetApiRequest
): Promise<MultiAssetApiResponse> {
  try {
    const response = await request<MultiAssetApiResponse, MultiAssetApiRequest>({
      operationId: OPERATION_IDS.multiAssetApi,
      requestData: params,
      connectionFrom: REST_CONNECTION_FROM,
    });
    return response;
  } catch (error) {
    console.error("Error fetching floating offers from OfferMart BRE API:", error);
    return {
      trace: {
        version: "1.4",
        modelName: "BRE_OfferMart_Interface",
        modelPath: "BRE_Service/INF06_Saving_Calculator_MultiAsset",
        requestId: "error",
      },
      output: {
        request_id: new Date().toISOString(),
        acms_offers: [],
        description: "Error fetching floating offers data",
        rms_coins: [],
        status_code: "500",
        offers_list: [],
        response_datetime: new Date().toISOString(),
      },
    };
  }
}
